import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, Flex, Grid, Heading, Text } from "@chakra-ui/react";
import {
  FaArrowRight,
  FaBullseye,
  FaChartLine,
  FaCheck,
  FaDatabase,
  FaLayerGroup,
  FaLightbulb,
  FaPercent,
} from "react-icons/fa6";
import { useReveal } from "../hooks/landingEffects";

const overlayBg = (gradient) => ({
  _before: {
    content: '""',
    position: "absolute",
    inset: 0,
    background: gradient,
    opacity: 0.7,
    pointerEvents: "none",
  },
});

const BentoIcon = ({ icon: Icon, gradient }) => (
  <Box
    position="relative"
    zIndex={1}
    w="38px"
    h="38px"
    borderRadius="11px"
    display="grid"
    placeItems="center"
    color="white"
    fontSize="15px"
    bg={gradient}
    boxShadow="0 8px 18px -6px rgba(99,102,241,0.5)"
    mb="14px"
  >
    <Icon />
  </Box>
);

const BentoTitle = ({ children }) => (
  <Heading
    as="h3"
    fontSize="18px"
    fontWeight={700}
    letterSpacing="-0.01em"
    mb="8px"
    color="white"
    position="relative"
    zIndex={1}
  >
    {children}
  </Heading>
);

const BentoBody = ({ children }) => (
  <Text
    fontSize="13px"
    color="rgba(230,236,245,0.7)"
    lineHeight="1.55"
    m={0}
    position="relative"
    zIndex={1}
  >
    {children}
  </Text>
);

// ============= Mini visualisations (per card) =============

const SalesSparkline = () => (
  <Box
    mt="auto"
    position="relative"
    zIndex={1}
    bg="rgba(0,0,0,0.25)"
    border="1px solid rgba(255,255,255,0.08)"
    borderRadius="14px"
    p="14px 16px"
    backdropFilter="blur(8px)"
  >
    <Flex justify="space-between" align="center" mb="6px">
      <Box>
        <Text
          fontSize="10px"
          textTransform="uppercase"
          letterSpacing="0.08em"
          color="rgba(230,236,245,0.65)"
          fontWeight={600}
        >
          Sales MTD · Demo Company @ PnP
        </Text>
        <Text
          fontSize="22px"
          fontWeight={700}
          letterSpacing="-0.01em"
          color="white"
          sx={{ fontVariantNumeric: "tabular-nums" }}
        >
          R3,420,189
        </Text>
      </Box>
      <Text
        as="span"
        fontSize="10px"
        px="8px"
        py="3px"
        borderRadius="9999px"
        bg="rgba(20,184,166,0.18)"
        color="#5eead4"
        fontWeight={700}
      >
        ▲ 4.2% YoY
      </Text>
    </Flex>
    <Box as="svg" viewBox="0 0 400 60" preserveAspectRatio="none" w="100%" h="60px">
      <defs>
        <linearGradient id="bArea" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#74ebd5" stopOpacity="0.5" />
          <stop offset="100%" stopColor="#74ebd5" stopOpacity="0" />
        </linearGradient>
        <linearGradient id="bStroke" x1="0" x2="1" y1="0" y2="0">
          <stop offset="0%" stopColor="#04aec1" />
          <stop offset="100%" stopColor="#ec4899" />
        </linearGradient>
      </defs>
      <path
        fill="url(#bArea)"
        d="M0,50 L40,42 L80,46 L120,38 L160,32 L200,36 L240,28 L280,20 L320,14 L360,18 L400,8 L400,60 L0,60 Z"
      />
      <path
        fill="none"
        stroke="url(#bStroke)"
        strokeWidth="2"
        d="M0,50 L40,42 L80,46 L120,38 L160,32 L200,36 L240,28 L280,20 L320,14 L360,18 L400,8"
      />
    </Box>
  </Box>
);

const StrikeRateBars = () => {
  const heights = [62, 84, 56, 92, 70, 95, 78];
  return (
    <Flex
      mt="auto"
      gap="6px"
      align="flex-end"
      h="56px"
      position="relative"
      zIndex={1}
      px="2px"
    >
      {heights.map((h, i) => (
        <Box
          key={i}
          flex="1"
          h={`${h}%`}
          bg="linear-gradient(180deg, #f472b6, #ec4899)"
          borderRadius="3px"
          boxShadow="0 4px 12px -4px rgba(236,72,153,0.55)"
          opacity={0.85 + (i / heights.length) * 0.15}
        />
      ))}
    </Flex>
  );
};

const ForecastLine = () => (
  <Box mt="auto" position="relative" zIndex={1} h="60px">
    <Box as="svg" viewBox="0 0 200 50" preserveAspectRatio="none" w="100%" h="100%">
      <defs>
        <linearGradient id="fcSolid" x1="0" x2="1" y1="0" y2="0">
          <stop offset="0%" stopColor="#04aec1" />
          <stop offset="100%" stopColor="#6366f1" />
        </linearGradient>
        <linearGradient id="fcArea" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#6366f1" stopOpacity="0.3" />
          <stop offset="100%" stopColor="#6366f1" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d="M0,40 L40,32 L80,28 L120,18 L120,50 L0,50 Z" fill="url(#fcArea)" />
      <path
        d="M0,40 L40,32 L80,28 L120,18"
        fill="none"
        stroke="url(#fcSolid)"
        strokeWidth="2"
      />
      <path
        d="M120,18 L160,12 L200,6"
        fill="none"
        stroke="#818cf8"
        strokeWidth="2"
        strokeDasharray="4 3"
        opacity="0.85"
      />
      <circle cx="120" cy="18" r="3.5" fill="#6366f1" stroke="white" strokeWidth="1" />
    </Box>
  </Box>
);

const RangingDots = () => {
  const pattern = [
    1, 1, 1, 0, 1, 1,
    1, 0, 1, 1, 1, 1,
    0, 1, 1, 1, 0, 1,
  ];
  return (
    <Box mt="auto" position="relative" zIndex={1}>
      <Grid templateColumns="repeat(6, 1fr)" gap="6px" maxW="160px">
        {pattern.map((filled, i) => (
          <Box
            key={i}
            w="10px"
            h="10px"
            borderRadius="50%"
            bg={
              filled
                ? "linear-gradient(135deg, #14b8a6, #0d9488)"
                : "rgba(255,255,255,0.06)"
            }
            boxShadow={filled ? "0 0 10px rgba(20,184,166,0.55)" : "none"}
            border={filled ? "none" : "1px solid rgba(255,255,255,0.18)"}
          />
        ))}
      </Grid>
    </Box>
  );
};

const MarginGauge = () => (
  <Box mt="auto" position="relative" zIndex={1}>
    <Box position="relative" h="22px">
      <Box
        position="absolute"
        left={0}
        right={0}
        top="8px"
        h="6px"
        borderRadius="3px"
        bg="linear-gradient(90deg, #ef4444 0%, #fbbf24 50%, #10b981 100%)"
        opacity={0.9}
      />
      <Box
        position="absolute"
        left="72%"
        top="0"
        transform="translateX(-50%)"
        w="3px"
        h="22px"
        bg="white"
        borderRadius="2px"
        boxShadow="0 0 10px rgba(255,255,255,0.7)"
      />
    </Box>
    <Flex
      justify="space-between"
      mt="6px"
      fontSize="9px"
      color="rgba(230,236,245,0.55)"
      fontWeight={600}
      textTransform="uppercase"
      letterSpacing="0.08em"
    >
      <Text>Loss</Text>
      <Text>Healthy</Text>
    </Flex>
  </Box>
);

const EdiPills = () => (
  <Flex mt="auto" gap="6px" wrap="wrap" position="relative" zIndex={1}>
    {["PnP", "Chk", "Dis", "Cli", "Mak"].map((r) => (
      <Flex
        key={r}
        align="center"
        gap="4px"
        bg="rgba(20, 184, 166, 0.18)"
        color="#5eead4"
        fontSize="10px"
        fontWeight={700}
        px="8px"
        py="3px"
        borderRadius="9999px"
        border="1px solid rgba(20,184,166,0.3)"
      >
        <FaCheck size={8} />
        {r}
      </Flex>
    ))}
  </Flex>
);

// ============= Click overlay CTA =============

const CtaOverlay = ({ show, onClose }) => {
  const navigate = useNavigate();
  const handleCta = (e) => {
    e.stopPropagation();
    onClose();
    navigate("/contact-us");
  };
  const handleBackdrop = (e) => {
    e.stopPropagation();
    onClose();
  };

  return (
    <Flex
      position="absolute"
      inset={0}
      align="center"
      justify="center"
      direction="column"
      gap="14px"
      bg="rgba(10, 14, 26, 0.78)"
      backdropFilter="blur(10px)"
      borderRadius="18px"
      zIndex={5}
      onClick={handleBackdrop}
      opacity={show ? 1 : 0}
      pointerEvents={show ? "auto" : "none"}
      transition="opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
      px="22px"
      textAlign="center"
    >
      <Text
        color="rgba(230,236,245,0.85)"
        fontSize="13px"
        fontWeight={500}
        maxW="220px"
        lineHeight="1.4"
      >
        See this — for your suppliers, your stores, your shelves.
      </Text>
      <Button
        type="button"
        onClick={handleCta}
        variant="unstyled"
        display="inline-flex"
        alignItems="center"
        gap="8px"
        bg="linear-gradient(135deg, #04aec1 0%, #6366f1 60%, #ec4899 120%)"
        backgroundSize="200% 200%"
        backgroundPosition="0% 50%"
        color="white"
        borderRadius="9999px"
        fontSize="13px"
        fontWeight={600}
        px="20px"
        py="10px"
        h="auto"
        boxShadow="0 10px 24px -8px rgba(99,102,241,0.55), inset 0 1px 0 rgba(255,255,255,0.25)"
        transition="all 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)"
        _hover={{
          transform: "translateY(-2px)",
          backgroundPosition: "100% 50%",
          boxShadow:
            "0 14px 32px -10px rgba(236,72,153,0.55), inset 0 1px 0 rgba(255,255,255,0.3)",
        }}
      >
        Contact us
        <FaArrowRight size={11} />
      </Button>
      <Text fontSize="10px" color="rgba(230,236,245,0.4)" mt="4px">
        Tap anywhere to close
      </Text>
    </Flex>
  );
};

// ============= Interactive cell =============

const InteractiveBentoCell = ({ children, sx, ...rest }) => {
  const [showCta, setShowCta] = useState(false);

  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={() => setShowCta(true)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          setShowCta(true);
        }
      }}
      borderRadius="18px"
      bg="linear-gradient(180deg, rgba(255,255,255,0.04), rgba(255,255,255,0.015))"
      border="1px solid rgba(255,255,255,0.08)"
      p="22px"
      position="relative"
      overflow="hidden"
      transition="all 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)"
      display="flex"
      flexDirection="column"
      color="white"
      textAlign="left"
      cursor="pointer"
      _hover={{
        transform: "translateY(-4px)",
        borderColor: "rgba(255,255,255,0.18)",
        boxShadow: "0 30px 60px -20px rgba(99,102,241,0.35)",
      }}
      sx={sx}
      {...rest}
    >
      {children}
      <CtaOverlay show={showCta} onClose={() => setShowCta(false)} />
    </Box>
  );
};

// ============= Page section =============

const BentoGrid = () => {
  const ref = useRef(null);
  useReveal(ref);

  return (
    <Box
      ref={ref}
      className="lp-reveal"
      id="product"
      as="section"
      maxW="1240px"
      mx="auto"
      px="24px"
      py="70px"
      scrollMarginTop="80px"
    >
      <Text
        as="span"
        display="inline-block"
        fontSize="11px"
        fontWeight={700}
        textTransform="uppercase"
        letterSpacing="0.16em"
        sx={{
          background: "linear-gradient(90deg, #74ebd5, #ec4899)",
          WebkitBackgroundClip: "text",
          backgroundClip: "text",
          color: "transparent",
        }}
        mb="14px"
      >
        The product
      </Text>
      <Heading
        as="h2"
        fontSize="clamp(28px, 3.5vw, 48px)"
        fontWeight={700}
        letterSpacing="-0.02em"
        lineHeight="1.05"
        mb="14px"
        maxW="720px"
        color="white"
      >
        Everything you need on one shelf.
      </Heading>
      <Text
        fontSize="16px"
        color="rgba(230,236,245,0.7)"
        maxW="580px"
        lineHeight="1.55"
        m={0}
      >
        Sales, strike rate, ranging, margin and EDI ingestion — wired together
        with the only data model that already understands South African retail.
      </Text>

      <Grid
        mt="36px"
        templateColumns={{ base: "repeat(2, 1fr)", lg: "repeat(6, 1fr)" }}
        gridAutoRows={{ base: "auto", lg: "220px" }}
        gap="16px"
      >
        <InteractiveBentoCell
          gridColumn={{ base: "span 2", lg: "span 4" }}
          gridRow={{ base: "span 1", lg: "span 2" }}
          p="26px 28px"
          minH={{ base: "320px", lg: "auto" }}
          sx={overlayBg(
            "linear-gradient(135deg, rgba(4,174,193,0.18), rgba(99,102,241,0.10) 60%, transparent)",
          )}
        >
          <BentoIcon icon={FaChartLine} gradient="linear-gradient(135deg, #04aec1, #6366f1)" />
          <BentoTitle>Sales, ranked &amp; explained</BentoTitle>
          <BentoBody>
            Track sales MTD, YoY change, units, SOH, days-cover and forecast —
            per article, per store, per retailer. Drill from the headline into
            the row that's hurting your week.
          </BentoBody>
          <SalesSparkline />
        </InteractiveBentoCell>

        <InteractiveBentoCell
          gridColumn="span 2"
          sx={overlayBg("linear-gradient(135deg, rgba(236,72,153,0.22), transparent)")}
        >
          <BentoIcon icon={FaBullseye} gradient="linear-gradient(135deg, #f472b6, #ec4899)" />
          <BentoTitle>Strike rate, weekly</BentoTitle>
          <BentoBody>
            Delivery performance with editable comments per reason — never
            repeat last week's mistake.
          </BentoBody>
          <StrikeRateBars />
        </InteractiveBentoCell>

        <InteractiveBentoCell
          gridColumn="span 2"
          sx={overlayBg("linear-gradient(135deg, rgba(99,102,241,0.22), transparent)")}
        >
          <BentoIcon icon={FaLightbulb} gradient="linear-gradient(135deg, #818cf8, #6366f1)" />
          <BentoTitle>Forecasts you trust</BentoTitle>
          <BentoBody>
            EOM forecast on every line, blended from your last 26 weeks plus
            retailer seasonality.
          </BentoBody>
          <ForecastLine />
        </InteractiveBentoCell>

        <InteractiveBentoCell
          gridColumn="span 2"
          sx={overlayBg("linear-gradient(135deg, rgba(20,184,166,0.22), transparent)")}
        >
          <BentoIcon icon={FaLayerGroup} gradient="linear-gradient(135deg, #14b8a6, #0d9488)" />
          <BentoTitle>Ranging at a glance</BentoTitle>
          <BentoBody>
            Which articles are active in which stores — by retailer, region,
            format.
          </BentoBody>
          <RangingDots />
        </InteractiveBentoCell>

        <InteractiveBentoCell
          gridColumn="span 2"
          sx={overlayBg("linear-gradient(135deg, rgba(251,191,36,0.20), transparent)")}
        >
          <BentoIcon icon={FaPercent} gradient="linear-gradient(135deg, #fbbf24, #f59e0b)" />
          <BentoTitle>Margin, line-by-line</BentoTitle>
          <BentoBody>
            Clean margin per supplier × retailer × article. Spot the
            loss-leaders before finance does.
          </BentoBody>
          <MarginGauge />
        </InteractiveBentoCell>

        <InteractiveBentoCell
          gridColumn="span 2"
          sx={overlayBg("linear-gradient(135deg, rgba(4,174,193,0.22), transparent)")}
        >
          <BentoIcon icon={FaDatabase} gradient="linear-gradient(135deg, #04aec1, #6366f1)" />
          <BentoTitle>EDI, ingested cleanly</BentoTitle>
          <BentoBody>
            Every Pick n Pay, Checkers, Dis-Chem, Clicks, Makro feed —
            validated, batched, queryable.
          </BentoBody>
          <EdiPills />
        </InteractiveBentoCell>
      </Grid>
    </Box>
  );
};

export default BentoGrid;
